import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

export interface CanvasStacAsset {
  href?: string;
  roles?: string[];
  type?: string;
  title?: string;
}

export interface CanvasStacItem {
  id: string;
  type: string;
  bbox?: number[];
  geometry?: unknown;
  properties?: Record<string, unknown>;
  assets?: Record<string, CanvasStacAsset>;
  links?: { rel?: string; href?: string }[];
}

export interface CanvasStacItemsPage {
  features: CanvasStacItem[];
  nextLink: string | null;
}

const DEFAULT_STAC_PAGE_LIMIT = 12;

@Injectable({ providedIn: 'root' })
export class CanvasStacApiService {
  private readonly http = inject(HttpClient);

  private normalizeBase(serverUrl: string): string {
    const trimmed = serverUrl.trim().replace(/\/+$/, '');
    if (!trimmed) {
      throw new Error('STAC server URL is not configured for this dataset.');
    }
    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed;
    }
    return `https://${trimmed}`;
  }

  async getStacCollection(
    datasetId: string,
    serverUrl: string,
  ): Promise<Record<string, unknown>> {
    const base = this.normalizeBase(serverUrl);
    const headers = new HttpHeaders({ Accept: 'application/json' });
    return firstValueFrom(
      this.http.get<Record<string, unknown>>(
        `${base}/stac/collections/${encodeURIComponent(datasetId)}`,
        { headers },
      ),
    );
  }

  async getStacItems(
    datasetId: string,
    serverUrl: string,
    options?: { limit?: number; nextUrl?: string | null; datetime?: string | null },
  ): Promise<CanvasStacItemsPage> {
    const base = this.normalizeBase(serverUrl);
    const limit = options?.limit ?? DEFAULT_STAC_PAGE_LIMIT;
    const url =
      options?.nextUrl?.trim() ||
      this.buildStacItemsUrl(base, datasetId, limit, options?.datetime);

    const headers = new HttpHeaders({ Accept: 'application/json' });
    const data = await firstValueFrom(
      this.http.get<{
        features?: CanvasStacItem[];
        links?: { rel?: string; href?: string }[];
      }>(url, { headers }),
    );

    const features = data.features ?? [];
    const rawNextLink =
      data.links?.find((l) => String(l.rel ?? '').toLowerCase() === 'next')
        ?.href ?? null;
    const nextLink =
      rawNextLink && features.length >= limit ? rawNextLink : null;

    return { features, nextLink };
  }

  private buildStacItemsUrl(
    base: string,
    datasetId: string,
    limit: number,
    datetime?: string | null,
  ): string {
    const params = new URLSearchParams({ limit: String(limit) });
    const interval = datetime?.trim();
    if (interval) {
      params.set('datetime', interval);
    }
    return `${base}/stac/collections/${encodeURIComponent(datasetId)}/items?${params.toString()}`;
  }
}
